import { create } from 'zustand'
import type { MyOrganizationResponse } from '../types/models.js'
import * as orgApi from '../api/organizations.js'

export type OrgStatus = 'idle' | 'loading' | 'empty' | 'ready' | 'error'

export interface OrgState {
  organizations: MyOrganizationResponse[]
  activeOrganization: MyOrganizationResponse | null
  status: OrgStatus
  error: string | null
}

interface OrgActions {
  load: () => Promise<void>
  create: (name: string, slug?: string) => Promise<void>
  setActive: (org: MyOrganizationResponse) => void
  clear: () => void
}

export type OrgStoreState = OrgState & OrgActions

function pickActive(
  orgs: MyOrganizationResponse[],
  current: MyOrganizationResponse | null,
): MyOrganizationResponse | null {
  if (orgs.length === 0) return null
  if (current && orgs.some(o => o.id === current.id)) return current
  return orgs[0] ?? null
}

// Monotonic generation guard. `clear()` bumps the generation, invalidating any
// in-flight load from a previous user/session so its stale response can neither
// mutate state nor clear the current in-flight promise.
let generation = 0
let inFlight: Promise<void> | null = null

export const useOrgStore = create<OrgStoreState>((set, get) => ({
  organizations: [],
  activeOrganization: null,
  status: 'idle',
  error: null,

  async load() {
    if (inFlight) return inFlight

    const { status, organizations, activeOrganization } = get()
    if (status === 'ready') return

    const gen = generation
    set({ status: 'loading', error: null })

    inFlight = orgApi
      .getMyOrganizations()
      .then(orgs => {
        if (gen !== generation) return
        set({
          organizations: orgs,
          activeOrganization: pickActive(orgs, activeOrganization),
          status: orgs.length > 0 ? 'ready' : 'empty',
          error: null,
        })
      })
      .catch((e: unknown) => {
        if (gen !== generation) return
        const message = e instanceof Error ? e.message : 'Failed to load organizations'
        // Do not lose an already-known non-empty org list on a transient error.
        set({
          status: organizations.length > 0 ? 'ready' : 'error',
          error: message,
        })
      })
      .finally(() => {
        if (gen === generation) inFlight = null
      })

    return inFlight
  },

  async create(name: string, slug?: string) {
    // Keep the current status so the onboarding form stays mounted (and can show
    // validation/errors) while the request is in flight. The page owns its own
    // submit spinner.
    set({ error: null })
    try {
      const result = await orgApi.createOrganization({ name, slug })
      const newOrg: MyOrganizationResponse = {
        id: result.organization.id,
        name: result.organization.name,
        slug: result.organization.slug,
        status: result.organization.status,
        role: result.membership.role,
        membershipId: result.membership.id,
        joinedAt: result.membership.createdAt,
      }
      const current = get().organizations
      const next = [newOrg, ...current.filter(o => o.id !== newOrg.id)]
      set({ organizations: next, activeOrganization: newOrg, status: 'ready', error: null })
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to create organization'
      set({ error: message })
      throw e
    }
  },

  setActive(org: MyOrganizationResponse) {
    set({ activeOrganization: org })
  },

  clear() {
    generation++
    inFlight = null
    set({ organizations: [], activeOrganization: null, status: 'idle', error: null })
  },
}))

/**
 * Imperative facade for non-React callers and tests.
 */
export const orgStore = {
  load: (): Promise<void> => useOrgStore.getState().load(),
  create: (name: string, slug?: string): Promise<void> => useOrgStore.getState().create(name, slug),
  clear: (): void => useOrgStore.getState().clear(),
}
